import React, { useEffect, useRef, useState } from 'react';

/**
 * Imperative, promise-based confirm dialog to replace blocking window.confirm().
 * Mount <ConfirmRoot/> once at app root, then anywhere:
 *   import { confirm } from '../utils/confirm';
 *   if (await confirm('Дали сте сигурни?')) { ... }
 *   if (await confirm({ message, title, confirmText, danger })) { ... }
 */
let opener = null;

export function confirm(opts) {
  const o = typeof opts === 'string' ? { message: opts } : (opts || {});
  if (!opener) {
    // Fallback if ConfirmRoot isn't mounted (e.g. tests)
    // eslint-disable-next-line no-alert
    return Promise.resolve(window.confirm(o.message || 'Дали сте сигурни?'));
  }
  return opener(o);
}

export function ConfirmRoot() {
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  useEffect(() => {
    opener = (o) => new Promise((resolve) => {
      resolver.current = resolve;
      setState({
        title: o.title || 'Потврда',
        message: o.message || 'Дали сте сигурни?',
        confirmText: o.confirmText || 'Потврди',
        cancelText: o.cancelText || 'Откажи',
        danger: o.danger !== false,
      });
    });
    return () => { opener = null; };
  }, []);

  const close = (result) => {
    setState(null);
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  };

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === 'Escape') close(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  if (!state) return null;

  return (
    <div className="confirm-overlay" onClick={() => close(false)}>
      <div
        className="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="confirm-title" className="confirm-title">{state.title}</h3>
        <p className="confirm-message">{state.message}</p>
        <div className="confirm-actions">
          <button type="button" className="btn btn-secondary" onClick={() => close(false)}>
            {state.cancelText}
          </button>
          <button
            type="button"
            className={`btn ${state.danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => close(true)}
            autoFocus
          >
            {state.confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
